/**
 * CompanyBrochureUploadPage — drag-and-drop a pitch deck / product catalog
 * PDF, let Claude extract the company profile, and auto-create / update
 * the Company record.
 *
 * Single-page flow in v1 (no preview-and-confirm step) — the backend runs
 * extract + commit in one shot. A review flow can layer on later.
 */
import React, { useCallback, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  CheckCircle2,
  FileText,
  Loader2,
  Mail,
  Phone,
  UploadCloud,
  Users,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import companyBrochureService from '../services/companyBrochureService';
import {
  CompanyBrochureProfile,
  CompanyBrochureUploadSuccess,
} from '../types/companyBrochure';

const MAX_BYTES = 25 * 1024 * 1024;

const formatMoney = (n?: number | null) => {
  if (n == null) return null;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
};

const ProfileCard: React.FC<{
  profile: CompanyBrochureProfile;
  summary?: CompanyBrochureUploadSuccess;
}> = ({ profile, summary }) => {
  const hq = [
    profile.headquarters_city,
    profile.headquarters_state,
    profile.headquarters_country,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          {profile.company_name || 'Unknown company'}
        </h2>
        {profile.tagline && (
          <p className="text-sm italic text-gray-600 mt-1">{profile.tagline}</p>
        )}
        {profile.website && (
          <a
            href={profile.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800 mt-1 inline-block"
          >
            {profile.website}
          </a>
        )}
      </div>

      {profile.description && (
        <p className="text-sm text-gray-700 leading-relaxed">
          {profile.description}
        </p>
      )}

      {(profile.industries && profile.industries.length > 0) && (
        <div className="flex flex-wrap gap-2">
          {profile.industries.map((ind) => (
            <span
              key={ind}
              className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700 border border-blue-200"
            >
              {ind}
            </span>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-gray-100">
        {hq && (
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">HQ</p>
            <p className="text-sm text-gray-900">{hq}</p>
          </div>
        )}
        {profile.founding_date && (
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Founded</p>
            <p className="text-sm text-gray-900">{profile.founding_date}</p>
          </div>
        )}
        {profile.employee_count != null && (
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Team size</p>
            <p className="text-sm text-gray-900">{profile.employee_count}</p>
          </div>
        )}
        {profile.funding_stage && (
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Funding stage</p>
            <p className="text-sm text-gray-900 capitalize">
              {profile.funding_stage.replace('_', ' ')}
            </p>
          </div>
        )}
        {profile.funding_amount_sought_usd != null && (
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Raising</p>
            <p className="text-sm text-gray-900">
              {formatMoney(profile.funding_amount_sought_usd)}
            </p>
          </div>
        )}
        {profile.current_revenue_usd != null && (
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Current revenue</p>
            <p className="text-sm text-gray-900">
              {formatMoney(profile.current_revenue_usd)}
            </p>
          </div>
        )}
      </div>

      {(profile.contact_email || profile.contact_phone) && (
        <div className="pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
            Contact
          </p>
          <div className="space-y-1 text-sm text-gray-800">
            {profile.contact_email && (
              <div className="flex items-center">
                <Mail className="w-4 h-4 mr-2 text-gray-400" />
                <a
                  href={`mailto:${profile.contact_email}`}
                  className="text-blue-600 hover:underline"
                >
                  {profile.contact_email}
                </a>
              </div>
            )}
            {profile.contact_phone && (
              <div className="flex items-center">
                <Phone className="w-4 h-4 mr-2 text-gray-400" />
                <span>{profile.contact_phone}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {profile.team_members && profile.team_members.length > 0 && (
        <div className="pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-2 flex items-center">
            <Users className="w-3.5 h-3.5 mr-1" />
            Team
          </p>
          <ul className="space-y-1">
            {profile.team_members.map((m, idx) => (
              <li key={`${m.name}-${idx}`} className="text-sm text-gray-800">
                <span className="font-medium">{m.name}</span>
                {m.role && <span className="text-gray-500"> — {m.role}</span>}
                {m.email && (
                  <>
                    {' '}
                    <a
                      href={`mailto:${m.email}`}
                      className="text-blue-600 hover:underline text-xs"
                    >
                      {m.email}
                    </a>
                  </>
                )}
                {m.linkedin_url && (
                  <>
                    {' '}
                    <a
                      href={m.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-xs"
                    >
                      LinkedIn
                    </a>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {profile.traction_signals && profile.traction_signals.length > 0 && (
        <div className="pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
            Traction
          </p>
          <ul className="list-disc list-inside text-sm text-gray-800 space-y-0.5">
            {profile.traction_signals.map((t, idx) => (
              <li key={idx}>{t}</li>
            ))}
          </ul>
        </div>
      )}

      {summary && (
        <div className="pt-4 border-t border-gray-100 bg-green-50 -mx-6 -mb-6 px-6 py-4 rounded-b-xl">
          <div className="flex items-center text-green-800 mb-2">
            <CheckCircle2 className="w-5 h-5 mr-2" />
            <p className="text-sm font-semibold">
              {summary.is_new_company
                ? `Created new company #${summary.company_id}`
                : `Matched existing company: ${summary.company_name}`}
            </p>
          </div>
          {summary.fields_updated.length > 0 && (
            <p className="text-xs text-green-700 mb-3">
              Fields populated: {summary.fields_updated.join(', ')}
            </p>
          )}
          <div className="flex gap-2">
            <Link
              to={`/companies`}
              className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
            >
              View companies
            </Link>
            <Link
              to={`/deal-rooms/${summary.deal_room_id}`}
              className="inline-flex items-center px-3 py-1.5 bg-white text-gray-800 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50"
            >
              Open Brochures deal room
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

const CompanyBrochureUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [profile, setProfile] = useState<CompanyBrochureProfile | null>(null);
  const [summary, setSummary] = useState<CompanyBrochureUploadSuccess | null>(null);
  const [needsReviewReason, setNeedsReviewReason] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFile = useCallback(async (file: File) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') {
      toast.error('Please upload a PDF');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`File too large (max ${MAX_BYTES / (1024 * 1024)} MB)`);
      return;
    }
    setBusy(true);
    setProfile(null);
    setSummary(null);
    setNeedsReviewReason(null);

    const res = await companyBrochureService.uploadBrochure(file);
    setBusy(false);
    if (res.error || !res.data) {
      toast.error(res.error || 'Upload failed');
      return;
    }
    if (res.data.status === 'ok') {
      setProfile(res.data.profile);
      setSummary(res.data);
      toast.success(
        res.data.is_new_company
          ? `Created new company: ${res.data.company_name}`
          : `Matched existing company: ${res.data.company_name}`
      );
    } else {
      setProfile(res.data.profile);
      setNeedsReviewReason(res.data.reason);
      toast('Extraction needs review — check the profile below', { icon: 'ℹ️' });
    }
  }, []);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/companies')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to companies
          </button>
        </div>

        <div>
          <h1 className="text-2xl font-bold text-gray-900">Upload company brochure</h1>
          <p className="text-gray-600 mt-1 text-sm">
            Drop a pitch deck, product catalog, or one-pager PDF. Claude
            extracts the company profile and creates or updates the record.
          </p>
        </div>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`bg-white border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
            dragOver
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-blue-400'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.currentTarget.value = '';
            }}
          />
          {busy ? (
            <div className="flex flex-col items-center text-gray-600">
              <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-3" />
              <p className="font-medium">Extracting with Claude Vision…</p>
              <p className="text-xs text-gray-500 mt-1">
                This can take 10-20 seconds for image-heavy decks.
              </p>
            </div>
          ) : (
            <>
              <UploadCloud className="w-10 h-10 mx-auto text-blue-600 mb-3" />
              <h2 className="text-lg font-semibold text-gray-900 mb-1">
                Drop a PDF here, or click to select
              </h2>
              <p className="text-sm text-gray-600">
                Pitch decks, product catalogs, one-pagers. Max 25 MB.
              </p>
            </>
          )}
        </div>

        {needsReviewReason && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
            <p className="font-semibold mb-1">Needs review</p>
            <p>{needsReviewReason}</p>
            <p className="mt-2 text-xs">
              The extracted profile is shown below, but nothing was saved yet.
              Upload a different PDF or add the company manually from the
              Companies page.
            </p>
          </div>
        )}

        {profile && (
          <ProfileCard profile={profile} summary={summary || undefined} />
        )}

        <div className="text-xs text-gray-500 pt-4 border-t border-gray-100">
          <FileText className="w-3.5 h-3.5 inline mr-1" />
          Uploaded PDFs are stored in a per-company "Brochures" deal room for
          later reference.
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CompanyBrochureUploadPage;
